import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Package, 
  ShoppingCart, 
  MapPin, 
  Calendar, 
  MessageSquare,
  Shield,
  Check,
  Loader
} from 'lucide-react';
import '../Booking.css';

const BookingForm = ({ product, formData, setFormData, onSubmit, loading }) => {
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const inputVariants = {
    focus: { scale: 1.01, transition: { type: 'spring', stiffness: 400 } },
    blur: { scale: 1, transition: { type: 'spring', stiffness: 400 } },
  };

  const formVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.08,
        delayChildren: 0.1,
      },
    },
  };

  const fieldVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { type: 'spring', stiffness: 300, damping: 30 },
    },
  };

  return (
    <motion.div 
      className="booking-form-wrapper"
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.4 }}
    >
      <motion.h2 
        className="form-title"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.1 }}
      >
        <Package className="form-title-icon" />
        Booking Details
      </motion.h2>

      <motion.form 
        onSubmit={onSubmit} 
        className="booking-form"
        variants={formVariants}
        initial="hidden"
        animate="visible"
      >
        <motion.div 
          className="form-group"
          variants={fieldVariants}
        >
          <label className="form-label">
            <Package size={16} className="label-icon" />
            Product
          </label>
          <motion.input
            type="text"
            value={`${product.name} - ${product.weight} (${product.purity})`}
            disabled
            className="form-input disabled"
            variants={inputVariants}
            whileFocus="focus"
            whileHover={{ scale: 1.005 }}
          />
        </motion.div>

        <motion.div 
          className="form-group"
          variants={fieldVariants}
        >
          <label className="form-label">
            <ShoppingCart size={16} className="label-icon" />
            Quantity
          </label>
          <motion.input
            type="number"
            name="quantity"
            min="1"
            max="100"
            value={formData.quantity}
            onChange={handleChange}
            className="form-input"
            required
            variants={inputVariants}
            whileFocus="focus"
            whileHover={{ scale: 1.005 }}
          />
          <motion.small 
            className="form-hint"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
          >
            Maximum 100 units per order
          </motion.small>
        </motion.div>

        <motion.div 
          className="form-group"
          variants={fieldVariants}
        >
          <label className="form-label">
            <MapPin size={16} className="label-icon" />
            Delivery Address
          </label>
          <motion.textarea
            name="deliveryAddress"
            rows="3"
            value={formData.deliveryAddress}
            onChange={handleChange}
            className="form-input textarea"
            placeholder="Enter your delivery address"
            required
            variants={inputVariants}
            whileFocus="focus"
            whileHover={{ scale: 1.005 }}
          />
        </motion.div>

        <motion.div 
          className="form-group"
          variants={fieldVariants}
        >
          <label className="form-label">
            <Calendar size={16} className="label-icon" />
            Preferred Delivery Date
          </label>
          <motion.input
            type="date"
            name="deliveryDate"
            value={formData.deliveryDate}
            onChange={handleChange}
            className="form-input"
            required
            min={new Date().toISOString().split('T')[0]}
            variants={inputVariants}
            whileFocus="focus"
            whileHover={{ scale: 1.005 }}
          />
        </motion.div>

        <motion.div 
          className="form-group"
          variants={fieldVariants}
        >
          <label className="form-label">
            <MessageSquare size={16} className="label-icon" />
            Special Instructions
          </label>
          <motion.textarea
            name="specialInstructions"
            rows="2"
            value={formData.specialInstructions}
            onChange={handleChange}
            className="form-input textarea"
            placeholder="Any special requirements..."
            variants={inputVariants}
            whileFocus="focus"
            whileHover={{ scale: 1.005 }}
          />
        </motion.div>

        <motion.div
          variants={fieldVariants}
          className="form-security-note"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.4 }}
        >
          <Shield size={14} />
          <span>Your information is secure and encrypted</span>
        </motion.div>

        <motion.button
          type="submit"
          disabled={loading}
          className="submit-btn"
          whileHover={!loading ? { scale: 1.02 } : {}}
          whileTap={!loading ? { scale: 0.98 } : {}}
          variants={fieldVariants}
        >
          <AnimatePresence mode="wait">
            {loading ? (
              <motion.span
                key="loading"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="flex items-center gap-2"
              >
                <Loader size={20} className="spinner" />
                Processing...
              </motion.span>
            ) : (
              <motion.span
                key="submit"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="flex items-center gap-2"
              >
                <Check size={20} />
                Confirm Booking
              </motion.span>
            )}
          </AnimatePresence>
        </motion.button>
      </motion.form>
    </motion.div>
  );
};

export default BookingForm;